/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { View } from "react-native";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";

import {
  LoadingDots,
  Text,
  UserCell,
  VideoModalLauncherIcon,
} from "UpduoLibrary";
import {
  formattedDateAndTimeString,
  formattedTimeString,
} from "config/Utilities";
import {
  skaleRed,
  skaleWebTextGray,
  skaleWebFillLightGray,
  colorWithOpacity,
} from "config/UpduoColors";

interface Column {
  id: "date" | "duration" | "teacher" | "learner" | "session";
  label: string;
  minWidth?: number;
  maxWidth?: number;
  alignItems?: "flex-start" | "center";
  format?: (value: number) => string;
}

const columns: Column[] = [
  {
    id: "date",
    label: "Date/Time",
    minWidth: 100,
    maxWidth: 100,
  },
  { id: "duration", label: "Duration", minWidth: 100, alignItems: "center" },
  { id: "teacher", label: "Teacher", minWidth: 100 },
  { id: "learner", label: "Learner", minWidth: 100 },
  {
    id: "session",
    label: "Session",
    minWidth: 120,
    maxWidth: 120,
    alignItems: "center",
  },
];

interface EventActivityTableProps {
  data: any;
  totalCount: number;
  fetch: any;
  pageInfo: any;
  summitId: String;
  loading: boolean;
}

const useStyles = makeStyles({
  root: {
    width: "100%",
    backgroundColor: skaleWebFillLightGray,
  },
  container: {
    maxHeight: 440,
  },
  header: {},
  tablecell: { fontFamily: "ObjectSans-Regular" },
});

function EventActivityRow({ item, itemIndex, classes }) {
  const formattedTime = formattedDateAndTimeString(
    new Date(item.created),
    true
  );

  return (
    <TableRow
      key={itemIndex}
      style={{
        backgroundColor: item.isCancelled
          ? colorWithOpacity(skaleRed, 0.2)
          : undefined,
      }}
    >
      <TableCell className={classes.tablecell}>{formattedTime}</TableCell>
      <TableCell className={classes.tablecell}>
        <View style={{ alignItems: "center" }}>
          {formattedTimeString(item.duration)}
        </View>
      </TableCell>
      <TableCell className={classes.tablecell}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <UserCell user={item.teacher} isGraphql={true} />
        </View>
      </TableCell>
      <TableCell className={classes.tablecell}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <UserCell user={item.learner} isGraphql={true} />
        </View>
      </TableCell>
      <TableCell className={classes.tablecell}>
        <View style={{ alignItems: "center" }}>
          {item.archiveUrl ? (
            <VideoModalLauncherIcon session={item} />
          ) : (
            <Text
              style={{
                marginLeft: 13,
                color: skaleWebTextGray,
              }}
            >
              {"-"}
            </Text>
          )}
        </View>
      </TableCell>
    </TableRow>
  );
}

export default function EventActivityTable({
  data,
  totalCount,
  fetch,
  pageInfo,
  summitId,
  loading,
}: EventActivityTableProps) {
  const classes = useStyles();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const loadingDots = (
    <View style={{ alignSelf: "center", marginTop: 20 }}>
      <LoadingDots size={10} />
    </View>
  );

  const _handlePageChange = (event: unknown, newPage: number) => {
    // newPage is 0 indexed
    if (data.length < (newPage + 1) * rowsPerPage && pageInfo.hasNextPage) {
      fetch({
        variables: {
          cursor: pageInfo.endCursor,
          getStaffSessionListRequest: {
            summitId,
          },
        },
      });
    }
    setPage(newPage);
  };

  const _handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    if (data.length < +event.target.value && pageInfo.hasNextPage) {
      fetch({
        variables: {
          cursor: pageInfo.endCursor,
          getStaffSessionListRequest: {
            summitId,
          },
        },
      });
    }
    setPage(0);
  };
  return (
    <Paper className={classes.root}>
      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label={"sticky table"} size={"small"}>
          <TableHead className={classes.header}>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{
                    fontWeight: "bold",
                    fontFamily: "ObjectSans-Regular",
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    alignItems: column.alignItems || "flex-start",
                    textAlign:
                      column.alignItems === "center" ? "center" : "start",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? loadingDots
              : data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, itemIndex) => [
                    <EventActivityRow
                      key={item.id}
                      item={item}
                      itemIndex={itemIndex}
                      classes={classes}
                    />,
                  ])}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={"div"}
        rowsPerPageOptions={[15, 25, 50]}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={_handlePageChange}
        onRowsPerPageChange={_handleChangeRowsPerPage}
      />
    </Paper>
  );
}
